import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_PAGES_AVG_TIME,
  CHART_KEY_DASHBOARD_PAGES_EFFECTIVE_RATE,
  CHART_KEY_DASHBOARD_PAGES_PAGEVIEWS,
  CHART_KEY_DASHBOARD_PAGES_SESSIONS,
  CHART_KEY_DASHBOARD_PAGES_TABLE,
  CHART_KEY_DASHBOARD_PAGES_TREND,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";

interface IPagesTabProps {
  attributes: ApiChartAttribute[];
}

function PagesTab({ attributes }: IPagesTabProps) {
  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PAGES_SESSIONS}
            staticAttributes={attributes}
            chart={{
              name: "Sessions",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Sessions",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PAGES_PAGEVIEWS}
            staticAttributes={attributes}
            chart={{
              name: "Pageviews",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Pageviews",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PAGES_EFFECTIVE_RATE}
            staticAttributes={attributes}
            chart={{
              name: "Effective Pageview Rate",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
                },
              ],
              displayOptions: {
                statTitle: "Effective Pageview Rate",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PAGES_AVG_TIME}
            staticAttributes={attributes}
            chart={{
              name: "Avg. Time on Page",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE],
                },
              ],
              displayOptions: {
                statTitle: "Avg. Time on Page",
              },
              comparison: true,
            }}
          />,
        ]}
      />

      <Box
        sx={{
          height: { xs: 320, md: 400 },
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_PAGES_TREND}
          staticAttributes={attributes}
          chart={{
            name: "Pageviews & Page Engagement Rate Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                chart: {
                  [ApiMetric.METRIC_PAGEVIEWS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_PAGES_TABLE}
          staticAttributes={attributes}
          chart={{
            name: "Pages Locations",
            dimension: [ApiDimension.DIMENSION_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_PAGEVIEWS_COUNT} DESC`,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                  ApiMetric.METRIC_PAGE_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_REVENUE,
                  ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  ApiMetric.METRIC_BOUNCE_RATE,
                  ApiMetric.METRIC_EXIT_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default PagesTab;
